import type { TechlineCaseEntity } from '@packages/domain/techline/cases';

import { WorksheetAnswerStatus } from '@packages/models/api';
import { isAfter, isBefore, isValid } from 'date-fns';

import type { WorksheetGroup } from '../../../worksheet-group-accordion/worksheet-group-accordion.types';

const MAX_RO_LENGTH = 10;
const MAX_MILEAGE = 9999999;
const MIN_DATE = new Date('2014-01-01');

/** A single field's validation rule. */
type FieldRule = {
    /** Whether the field is required. `true` = always; function = conditional. */
    required: boolean | ((cd: TechlineCaseEntity) => boolean);

    /** Error message — either a i18n key or a plain string. */
    requiredMessage: string;

    /** Optional format/range check, called only when the value is non-empty. Returns an error message or undefined. */
    validate?: (value: unknown) => string | undefined;
};

/** The validation rules for a single step. */
type StepRules = {
    /** i18n base path prepended to all error keys in the step. */
    translateBasePath: string;

    /** Field name → validation rule. */
    fields: Record<string, FieldRule>;
};

/**
 * Returns true when a field is considered blank for required-field purposes.
 *
 * @param value - the field value to check
 * @returns whether the value is empty
 */
export function isFieldEmpty(value: unknown): boolean {
    if (value === undefined || value === null) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (typeof value === 'number') {
        return value === 0;
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    return false;
}

/**
 * Evaluates whether a rule is currently required for the given case.
 *
 * @param rule - the field rule to evaluate
 * @param caseDetails - the current case entity
 * @returns whether the rule is required
 */
function isRuleRequired(rule: FieldRule, caseDetails: TechlineCaseEntity): boolean {
    return typeof rule.required === 'function' ? rule.required(caseDetails) : rule.required;
}

/**
 * Runs every rule in a step config against the case details.
 * Returns the step fields that are currently invalid and the error message for each.
 *
 * @param stepRules - the step's validation rules
 * @param caseDetails - the current case entity
 * @returns field name → error message for invalid fields
 */
function validateStep(stepRules: StepRules, caseDetails: TechlineCaseEntity): Record<string, string> {
    const errors: Record<string, string> = {};
    const { translateBasePath, fields } = stepRules;

    for (const [field, rule] of Object.entries(fields)) {
        if (!isRuleRequired(rule, caseDetails)) {
            continue;
        }

        const value = (caseDetails as unknown as Record<string, unknown>)[field];

        if (isFieldEmpty(value)) {
            errors[field] = `${translateBasePath}.${rule.requiredMessage}`;
            continue;
        }

        const error = rule.validate?.(value);
        if (error) {
            errors[field] = `${translateBasePath}.${error}`;
        }
    }

    return errors;
}

/** Validation rules for the Concern Details step. */
const concernDetailsRules: StepRules = {
    translateBasePath: 'techline:concernDetails',
    fields: {
        repairOrderNumber: {
            required: true,
            requiredMessage: 'repairOrder.error',
            validate: maxLength(MAX_RO_LENGTH, 'repairOrder.lengthError'),
        },
        repairOrderOpenDate: {
            required: true,
            requiredMessage: 'repairOpenDate.error',
            validate: dateRange,
        },
        mileage: {
            required: true,
            requiredMessage: 'mileage.error',
            validate: mileageRange('mileage.maxError'),
        },
        customerConcern: {
            required: true,
            requiredMessage: 'customerConcern.error',
        },

        // Conditional: "Has been able to duplicate"
        technician5StepsBeforeFailureDuplication: {
            required: (cd) => cd?.hasBeenAbleToDuplicate === true,
            requiredMessage: 'stepsToDuplicateTextArea.errorMessage',
        },

        // Conditional: "Vehicle has been in before"
        previousRONumber: {
            required: (cd) => cd?.vehicleHasBeenInBefore === true,
            requiredMessage: 'previousRepairOrderNumberText.errorMessage',
            validate: maxLength(MAX_RO_LENGTH, 'previousRepairOrderNumberText.invalidFormat'),
        },
        previousMileage: {
            required: (cd) => cd?.vehicleHasBeenInBefore === true,
            requiredMessage: 'previousMileageText.errorMessage',
            validate: mileageRange('previousMileageText.invalidFormat'),
        },
        previousDiagnosisAndRepairs: {
            required: (cd) => cd?.vehicleHasBeenInBefore === true,
            requiredMessage: 'previousDiagnosisAndRepairTextArea.errorMessage',
        },

        // Conditional: "Were parts replaced"
        whatPartsWereReplaced: {
            required: (cd) => cd?.werePartsReplaced === true,
            requiredMessage: 'partsReplacedText.errorMessage',
        },
        didTheConditionImprove: {
            required: (cd) => cd?.werePartsReplaced === true,
            requiredMessage: 'conditionImproveTextArea.errorMessage',
        },
    },
};

/** Validation rules for the Tech Description step. */
const techDescriptionRules: StepRules = {
    translateBasePath: 'techline:techDescription',
    fields: {
        technicianDescription: {
            required: true,
            requiredMessage: 'technicianDescription.errorMessage',
        },
    },
};

/** Step rules keyed by step name. */
const STEP_RULES: Record<string, StepRules> = {
    concernDetails: concernDetailsRules,
    techDescription: techDescriptionRules,
};

/**
 * Returns the field names that are currently required for a step.
 *
 * @param step - the step key (e.g. 'concernDetails')
 * @param caseDetails - the current case entity
 * @returns array of required field names
 */
export function getRequiredFields(step: string, caseDetails: TechlineCaseEntity): string[] {
    const rules = STEP_RULES[step];
    if (!rules) {
        return [];
    }
    return Object.entries(rules.fields)
        .filter(([, rule]) => isRuleRequired(rule, caseDetails))
        .map(([field]) => field);
}

/**
 * Returns field → error message for all invalid concern details fields.
 *
 * @param cd - the current case entity
 * @returns field name → error message for invalid fields
 */
export function validateConcernDetails(cd: TechlineCaseEntity): Record<string, string> {
    return validateStep(concernDetailsRules, cd);
}

/**
 * Returns field → error message for all invalid tech description fields.
 *
 * @param cd - the current case entity
 * @returns field name → error message for invalid fields
 */
export function validateTechDescription(cd: TechlineCaseEntity): Record<string, string> {
    return validateStep(techDescriptionRules, cd);
}

/**
 * Returns error key to reason string for all worksheet-level errors.
 *
 * @param worksheetGroupData - the worksheet groups attached to the case
 * @param worksheetValidationResults - per-submission pass/fail map keyed by submissionId
 * @param hasCompletedWorksheets - whether the case has at least one completed worksheet
 * @returns error key to reason string for invalid worksheets
 */
export function validateWorksheets(
    worksheetGroupData: WorksheetGroup[] | undefined,
    worksheetValidationResults: Record<string, boolean> | undefined,
    hasCompletedWorksheets: boolean
): Record<string, string> {
    const errors: Record<string, string> = {};

    if (!hasCompletedWorksheets) {
        errors.noWorksheets = 'required';
    }

    if (worksheetGroupData?.length) {
        const hasMissingRequired = worksheetGroupData.some((group) =>
            group.worksheetTitles?.some((worksheet) => {
                if (!worksheet.submissionId || worksheet.status !== WorksheetAnswerStatus.Draft) {
                    return false;
                }
                return worksheetValidationResults?.[worksheet.submissionId] === false;
            })
        );
        if (hasMissingRequired) {
            errors.missingRequiredFields = 'invalid';
        }
    }

    return errors;
}

/**
 * Validates that a date is valid, not in the future, and not before {@link MIN_DATE}.
 *
 * @param value - the raw date value to validate
 * @returns an error message if invalid, otherwise undefined
 */
function dateRange(value: unknown): string | undefined {
    const date = new Date(String(value).split('T')[0]);
    if (!isValid(date)) {
        return 'repairOpenDate.validationError';
    }
    if (isAfter(date, new Date())) {
        return 'repairOpenDate.futureDateError';
    }
    if (isBefore(date, MIN_DATE)) {
        return 'repairOpenDate.pastDateError';
    }
}

/**
 * Creates a validator that rejects values exceeding the given character length.
 *
 * @param max - the maximum allowed character length
 * @param message - the error message to return on failure
 * @returns a validation function
 */
function maxLength(max: number, message: string) {
    return (value: unknown): string | undefined => {
        if (String(value).trim().length > max) {
            return message;
        }
    };
}

/**
 * Creates a validator that rejects mileage values that are not a number or exceed {@link MAX_MILEAGE}.
 *
 * @param message - the error message to return on failure
 * @returns a validation function
 */
function mileageRange(message: string) {
    return (value: unknown): string | undefined => {
        const num = Number.parseInt(String(value).replace(/,/g, ''), 10);
        if (Number.isNaN(num) || num > MAX_MILEAGE) {
            return message;
        }
    };
}
